// src/pages/help/HelpGlossary.tsx
// Página de Glosario de términos

import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { glossaryTerms } from '../../data/helpCenterData';

import Navbar from '../../components/navbar/navbar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

export default function HelpGlossary() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeLetter, setActiveLetter] = useState<string>('all');

  // Ordenar términos alfabéticamente
  const sortedTerms = useMemo(() => {
    return [...glossaryTerms].sort((a, b) => a.term.localeCompare(b.term, 'es'));
  }, []);

  // Obtener letras disponibles
  const availableLetters = useMemo(() => {
    const letters = new Set(sortedTerms.map(term => term.term[0].toUpperCase()));
    return Array.from(letters).sort();
  }, [sortedTerms]);

  // Filtrar términos
  const filteredTerms = useMemo(() => {
    return sortedTerms.filter(term => {
      const matchLetter = activeLetter === 'all' || term.term[0].toUpperCase() === activeLetter;
      const matchSearch = searchQuery === '' ||
        term.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
        term.definition.toLowerCase().includes(searchQuery.toLowerCase());
      return matchLetter && matchSearch;
    });
  }, [sortedTerms, activeLetter, searchQuery]);

  // Agrupar por letra
  const groupedTerms = useMemo(() => {
    const groups: { [key: string]: typeof glossaryTerms } = {};
    filteredTerms.forEach(term => {
      const letter = term.term[0].toUpperCase();
      if (!groups[letter]) {
        groups[letter] = [];
      }
      groups[letter].push(term);
    });
    return groups;
  }, [filteredTerms]);

  // Alfabeto completo para navegación
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  return (
    <>
      <Navbar transparent={false} />

      {/* Header */}
      <section className="py-5 bg-primary">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Centro de Ayuda', path: '/ayuda' },
              { label: 'Glosario' }
            ]}
            light={true}
          />

          <div className="row align-items-center mt-4">
            <div className="col-auto">
              <span className="d-inline-flex align-items-center justify-content-center bg-white rounded-circle" style={{ width: '80px', height: '80px' }}>
                <i className="fa-solid fa-book fa-2x text-primary"></i>
              </span>
            </div>
            <div className="col">
              <h1 className="text-white mb-2">Glosario de Términos</h1>
              <p className="text-white-50 mb-0">Definiciones de los términos más utilizados en la plataforma</p>
            </div>
          </div>
        </div>
      </section>

      {/* Búsqueda y navegación alfabética */}
      <section className="py-4 bg-light border-bottom sticky-top" style={{ zIndex: 100 }}>
        <div className="container">
          {/* Buscador */}
          <div className="row mb-3">
            <div className="col-lg-6 mx-auto">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <i className="fa-solid fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Buscar término..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchQuery('')}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Navegación alfabética */}
          <div className="d-flex flex-wrap justify-content-center gap-1">
            <button
              className={`btn btn-sm ${activeLetter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveLetter('all')}
            >
              Todos
            </button>
            {alphabet.map(letter => {
              const isAvailable = availableLetters.includes(letter);
              return (
                <button
                  key={letter}
                  className={`btn btn-sm ${
                    activeLetter === letter
                      ? 'btn-primary'
                      : isAvailable
                        ? 'btn-outline-primary'
                        : 'btn-outline-secondary disabled'
                  }`}
                  onClick={() => isAvailable && setActiveLetter(letter)}
                  disabled={!isAvailable}
                  style={{ minWidth: '36px' }}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lista de términos */}
      <section className="py-5">
        <div className="container">
          {filteredTerms.length === 0 ? (
            <div className="text-center py-5">
              <i className="fa-solid fa-search fa-4x text-muted mb-3"></i>
              <h5>No se encontraron términos</h5>
              <p className="text-muted">
                No hay términos que coincidan con tu búsqueda.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setSearchQuery('');
                  setActiveLetter('all');
                }}
              >
                Ver todos los términos
              </button>
            </div>
          ) : (
            <>
              {/* Contador */}
              <p className="text-muted mb-4">
                Mostrando {filteredTerms.length} de {glossaryTerms.length} términos
              </p>

              {/* Términos agrupados por letra */}
              {activeLetter === 'all' ? (
                Object.entries(groupedTerms).map(([letter, terms]) => (
                  <div key={letter} className="mb-5" id={`letter-${letter}`}>
                    <div className="d-flex align-items-center mb-3">
                      <span className="d-inline-flex align-items-center justify-content-center bg-primary text-white rounded-circle fw-bold" style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}>
                        {letter}
                      </span>
                      <hr className="flex-grow-1 ms-3" />
                    </div>

                    <div className="row g-4">
                      {terms.map((term, index) => (
                        <div className="col-lg-6" key={index}>
                          <div className="card border-0 shadow-sm h-100">
                            <div className="card-body">
                              <h5 className="card-title text-primary mb-2">
                                <i className="fa-solid fa-bookmark me-2"></i>
                                {term.term}
                              </h5>
                              <p className="card-text text-muted mb-0">
                                {term.definition}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="row g-4">
                  {filteredTerms.map((term, index) => (
                    <div className="col-lg-6" key={index}>
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <h5 className="card-title text-primary mb-2">
                            <i className="fa-solid fa-bookmark me-2"></i>
                            {term.term}
                          </h5>
                          <p className="card-text text-muted mb-0">
                            {term.definition}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Enlaces de ayuda */}
          <div className="card border-0 shadow-sm mt-5">
            <div className="card-body p-4">
              <div className="row align-items-center">
                <div className="col-lg-8 mb-3 mb-lg-0">
                  <h5 className="mb-1">¿Falta algún término?</h5>
                  <p className="text-muted mb-0">
                    Si hay algún término que no encuentras en el glosario, contáctanos para agregarlo.
                  </p>
                </div>
                <div className="col-lg-4 text-lg-end">
                  <Link to="/contact" className="btn btn-primary">
                    <i className="fa-solid fa-envelope me-2"></i>
                    Sugerir término
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Navegación rápida */}
          <div className="card border-0 shadow-sm mt-4">
            <div className="card-header bg-white">
              <h6 className="mb-0">
                <i className="fa-solid fa-compass me-2"></i>
                Más recursos de ayuda
              </h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <Link to="/ayuda" className="text-decoration-none">
                    <div className="d-flex align-items-center p-3 border rounded hover-bg-light">
                      <i className="fa-solid fa-home fa-lg text-primary me-3"></i>
                      <div>
                        <h6 className="mb-0 text-dark">Centro de Ayuda</h6>
                        <small className="text-muted">Página principal</small>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="col-md-4">
                  <Link to="/ayuda/faq" className="text-decoration-none">
                    <div className="d-flex align-items-center p-3 border rounded hover-bg-light">
                      <i className="fa-solid fa-circle-question fa-lg text-primary me-3"></i>
                      <div>
                        <h6 className="mb-0 text-dark">Preguntas Frecuentes</h6>
                        <small className="text-muted">Dudas comunes</small>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="col-md-4">
                  <Link to="/ayuda/seccion/getting-started" className="text-decoration-none">
                    <div className="d-flex align-items-center p-3 border rounded hover-bg-light">
                      <i className="fa-solid fa-rocket fa-lg text-primary me-3"></i>
                      <div>
                        <h6 className="mb-0 text-dark">Primeros Pasos</h6>
                        <small className="text-muted">Guía de inicio</small>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />

      <style>{`
        .hover-bg-light:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </>
  );
}
